'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useTheme } from 'next-themes'
import { BellRing, KeyRound, LogOut, Monitor, Moon, Sun, UserRound } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@elmnts/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@elmnts/ui/tooltip'
import { hrefDaSecao, ROTA_DA_CONTA } from '@/app/(private)/conta/_lib/abasDaConta'
import { useAuth } from '@/providers/AuthContext'
import { useProfileAvatar } from '@/hooks/useProfileAvatar'
import { UserAvatar } from '@/components/UserAvatar'
import { cn } from '@/lib/utils'

const THEME_OPTIONS = [
  { value: 'light', icon: Sun, label: 'Claro' },
  { value: 'system', icon: Monitor, label: 'Sistema' },
  { value: 'dark', icon: Moon, label: 'Escuro' },
] as const

export function UserRailButton() {
  const { user, logout } = useAuth()
  const { icon, color } = useProfileAvatar()
  const { theme, setTheme } = useTheme()
  const [open, setOpen] = useState(false)

  // Close popover on navigation actions; keep open for theme switching.
  // - Links de navegação ("Notificações", "Alterar senha"): close before navigation
  // - "Sair" logout: close explicitly (not incidental)
  // - Theme buttons: no close (UX: allow comparisons before closing)
  const handleNavigate = () => {
    setOpen(false)
  }

  const handleLogout = () => {
    setOpen(false)
    logout()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            {/* As classes de interação ficam no <button>, não no avatar: quem
                reage ao hover/aberto é o controle, não a arte. */}
            <button
              type="button"
              aria-label="Menu do usuário"
              className="rounded-lg transition-opacity hover:opacity-90 data-[state=open]:ring-2 data-[state=open]:ring-sidebar-ring"
            >
              <UserAvatar name={user?.name} icon={icon} color={color} size="sm" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">Menu do usuário</TooltipContent>
      </Tooltip>

      <PopoverContent side="right" align="end" className="w-64 p-2">
        <div className="flex items-center gap-2 px-2 py-1.5">
          <UserAvatar name={user?.name} icon={icon} color={color} size="sm" />
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-semibold text-foreground">{user?.name ?? 'Usuário'}</span>
            <span className="truncate text-xs text-muted-foreground">@{user?.username ?? ''}</span>
          </div>
        </div>

        <div className="my-1 h-px bg-border" />

        <div className="flex items-center justify-center gap-1 py-1">
          {THEME_OPTIONS.map(({ value, icon: Icon, label }) => (
            <button
              key={value}
              type="button"
              title={label}
              aria-label={`Tema ${label}`}
              onClick={() => setTheme(value)}
              data-active={theme === value || undefined}
              className={cn(
                'flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                'data-[active=true]:bg-primary data-[active=true]:text-primary-foreground',
              )}
            >
              <Icon className="size-3.5" />
            </button>
          ))}
        </div>

        <div className="my-1 h-px bg-border" />

        {/* Única entrada pro shell `/conta` — sem ela, "Aparência" só era achada
            por quem clicasse em "Notificações" e reparasse nas abas. Aponta pra
            RAIZ do shell (Perfil), onde a régua das 5 abas fica visível; as
            seções NÃO viram itens soltos aqui, senão o menu duplica a nav do shell.
            Todo link daqui sai do mapa (`conta/_lib/abasDaConta`). */}
        <Link
          href={ROTA_DA_CONTA}
          onClick={handleNavigate}
          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <UserRound className="size-4" />
          Minha conta
        </Link>

        {/* Fica DEPOIS de "Minha conta" (geral → atalho) e continua apontando
            direto pra seção: era o item já descoberto, enterrá-lo seria regressão.
            Pelo mapa, não pelo endereço da subrota antiga (hoje redirect). */}
        <Link
          href={hrefDaSecao('notificacoes')}
          onClick={handleNavigate}
          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <BellRing className="size-4" />
          Notificações
        </Link>

        {/* Seção Senha de Minha conta, NÃO a tela forçada `/change-password`: aquela é o
            enquadramento de senha temporária ("Sua senha é temporária — defina
            uma nova para continuar") e empurra pra `/chatbot` ao salvar. Quem
            chega por este menu tem senha normal e está no meio de outra coisa —
            as duas afirmações seriam falsas pra ele. A tela forçada é alcançada
            só pelo redirect pós-login (o `window.location.assign` no fim do
              `handleLogin` de `login/page.tsx`). */}
        <Link
          href={hrefDaSecao('senha')}
          onClick={handleNavigate}
          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <KeyRound className="size-4" />
          Alterar senha
        </Link>

        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-destructive transition-colors hover:bg-destructive/10"
        >
          <LogOut className="size-4" />
          Sair
        </button>
      </PopoverContent>
    </Popover>
  )
}
